import {
    type GetLabelType,
    type GetOptionalLabelType,
    type UseLabelType,
    type UseOptionalLabelType,
    getLabelFn,
    getOptionalLabelFn,
    getUseLabel,
    getUseOptionalLabel,
} from "../../../../common-deprecated/hooks/useLabel";
import type { GradeSelectorStateType } from "../redux/store";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import type { GradeSelectorSettingsType } from "../redux/reducers/GradeSelectorSettingsReducer";
import {
    type LabelComponentType,
    getLabelComponent,
    getMemoLabelComponent,
} from "../../../../common-deprecated/components/label/Label";
import { T1Labels } from "../../../../common-deprecated/utils/t1labels";

const a11yLabels = {
    // SR only a11y specific
    gradeSelectorA11yButtonToggleFuelInfo: "Toggle fuel info",
    gradeSelectorA11yButtonScrollPrevious: "Scroll Previous",
    gradeSelectorA11yButtonScrollNext: "Scroll Next",
    gradeSelectorA11yNavFilter: "Filters",
};

export const gradeSelectorLabels = {
    ...T1Labels,
    ...a11yLabels,

    // Grade selector
    gradeSelectorMainTitle: "Which <strong>{model}</strong> will you choose?",
    gradeSelectorMainSubtitle: "See the prices and costs",
    gradeSelectorGradeCtaButton: "Explore & configure",
    gradeSelectorTabsHighlights: "Highlights",
    gradeSelectorCompare: "Compare",

    gradeSelectorEngineChange: "Change engine",
    gradeSelectorEngineSelected: "Selected engine",

    // Engine specs
    gradeSelectorSpecsUnavailable: "Not available",
    gradeSelectorSpecsOptional: "Optional",
    gradeSelectorSpecsIncluded: "Included",
    gradeSelectorSpecWheeldrive: "Wheel drive",
    gradeSelectorSpecTransmission: "Transmission",
    gradeSelectorSpecEnginePrice: "Engine price",
    gradeSelectorSpecEcoTagTitle: "CO2 Class",
    gradeSelectorSpecEcoBadgeTitle: "Etiquette",

    // Grade switcher
    gradeSelectorKeepGrade: "Keep",
    gradeSelectorSelectGrade: "Select",
    gradeSelectorGradeSwitcherTitle: "All {model} grades",

    // Fueltypes
    gradeSelectorFuelTypeStandard: "Standard",
    gradeSelectorFuelTypeAll: "All",

    // Exchange rate
    gradeSelectorExchangeRate: "Fixed conversion rate: {primaryPrice} = {secondaryPrice}",

    // Filters
    gradeSelectorFilterAllGradesButton: "All grades",
    gradeSelectorFilterShowMore: "More filters",
    gradeSelectorFilterShowLess: "Fewer filters",
    gradeSelectorFilterSeeResultsDisabled: "See results",
    gradeSelectorFilterSeeResults: "See {amount} results",
    gradeSelectorFilterSeeResult: "See 1 result",
    gradeSelectorFilterEngineButton: "Engine",
    gradeSelectorFilterWheeldriveButton: "Wheel drive",
    gradeSelectorFilterTransmissionButton: "Transmission",
    gradeSelectorFilterColourButton: "Colour",
    gradeSelectorFilterPriceButton: "Price",
    gradeSelectorFilterSortButton: "Sort",
    gradeSelectorFilterPopularFeaturesButton: "Popular features",
    gradeSelectorFilterLcvButton: "Van features",
    gradeSelectorAllFiltersButton: "All filters",
    gradeSelectorActiveFilters: "{amount} active filters",
    gradeSelectorActiveFilter: "1 active filter",
    gradeSelectorRemoveFilters: "Remove all",

    gradeSelectorPriceFilterLowest: "From {min}",
    gradeSelectorPriceFilterHighest: "up to {max}",

    gradeSelectorSortingCashPrice: "Cash price",
    gradeSelectorSortingWltp: "WLTP CO2",
    gradeSelectorSortingFuelEfficiency: "Fuel Efficiency",
    gradeSelectorSortingAsc: "Lowest",
    gradeSelectorSortingDesc: "Highest",

    gradeSelectorEngineOptions: "Engine options",
    gradeSelectorEngineInfo: "Engine info",

    // Legend
    gradeSelectorLegendTitle: "Legend",
    gradeSelectorLegendStandard: "Standard",
    gradeSelectorLegendOptional: "Optional",
    gradeSelectorLegendNotApplicable: "Not applicable",

    // Retailer specific Toyota
    gradeSelectorRetailerChooseYour: "Choose your {model} grade",
    gradeSelectorRetailerGradeHighlightsCtaButton: "All highlights",
    gradeSelectorRetailerGradeSpecsCtaButton: "All specifications",
    gradeSelectorRetailerCompareThisGrade: "Compare this grade",
    gradeSelectorRetailerCompareSlotsAreFull: "Compare slots are full",
    gradeSelectorRetailerViewCompare: "View compare",

    // Retailer specific Lexus
    gradeSelectorRetailerBackToModelSelection: "Back to model selection",

    // No results
    gradeSelectorNoResultsTitle: "No results found",
    gradeSelectorNoResultsSubtitle: "Please broaden your filters or reset them to start over",
    gradeSelectorNoResultsButton: "Reset all filters",
};

export type GradeSelectorLabelType = keyof typeof gradeSelectorLabels;
type GradeSelectorLabelParamType = Partial<
    Record<"model" | "num" | "primaryPrice" | "secondaryPrice" | "amount" | "filter", string>
>;

type GradeSelectorLabelStateType = {
    commonSettings: CommonSettingsType;
    gradeSelectorSettings: GradeSelectorSettingsType;
};

export const useGradeSelectorLabel = getUseLabel(
    (state: GradeSelectorStateType) => state.gradeSelectorSettings.labels,
    (state: GradeSelectorStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as UseLabelType<GradeSelectorLabelType, GradeSelectorLabelParamType>;

export const useGradeSelectorOptionalLabel = getUseOptionalLabel(
    (state: GradeSelectorStateType) => state.gradeSelectorSettings.labels,
    (state: GradeSelectorStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as UseOptionalLabelType<GradeSelectorLabelType, GradeSelectorLabelParamType>;

export const getGradeSelectorLabel = getLabelFn(
    (state: GradeSelectorLabelStateType) => state.gradeSelectorSettings.labels,
    (state: GradeSelectorLabelStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as GetLabelType<GradeSelectorLabelStateType, GradeSelectorLabelType, GradeSelectorLabelParamType>;

export const getGradeSelectorOptionalLabel = getOptionalLabelFn(
    (state: GradeSelectorLabelStateType) => state.gradeSelectorSettings.labels,
    (state: GradeSelectorLabelStateType) => !!state.commonSettings.query.showDictionaryKeys,
) as GetOptionalLabelType<GradeSelectorLabelStateType, GradeSelectorLabelType, GradeSelectorLabelParamType>;

export const GradeSelectorLabel = getLabelComponent(useGradeSelectorLabel) as LabelComponentType<
    GradeSelectorLabelType,
    GradeSelectorLabelParamType
>;

export const GradeSelectorMemoLabel = getMemoLabelComponent(useGradeSelectorLabel) as LabelComponentType<
    GradeSelectorLabelType,
    GradeSelectorLabelParamType
>;
